import { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyRound, Plus, Search, ShieldCheck, Trash2, UserCog, Users, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import {
  createUser,
  deleteUser,
  getUsers,
  updateUserPassword,
  updateUsername,
  updateUserRole,
} from '../api/authApi';
import { getAllMachines, getCompanies } from '../api/api';
import { ActionDrawer, ConfirmDialog, Toast } from '../components/ui';
import { getUser, setUser } from '../utils/auth';
import styles from './UserManagement.module.scss';

const validateUsername = username => {
  if (!username) {
    return 'Username is required.';
  }
  if (username.length < 3) {
    return 'Username must have at least 3 characters.';
  }
  if (username.length > 30) {
    return 'Username must have no more than 30 characters.';
  }
  if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
    return 'Use only letters, numbers, underscores, and dashes.';
  }
  return '';
};

const validatePassword = password =>
  !password || password.length < 6 ? 'Password must have at least 6 characters.' : '';

function UserManagement() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editType, setEditType] = useState('username');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formError, setFormError] = useState('');
  const [companies, setCompanies] = useState([]);
  const [machines, setMachines] = useState([]);
  const [createForm, setCreateForm] = useState({
    username: '',
    password: '',
    role: 'user',
    company_ids: [],
    machine_ids: [],
  });
  const [editForm, setEditForm] = useState({
    currentPassword: '',
    newPassword: '',
    newUsername: '',
    role: '',
    company_ids: [],
    machine_ids: [],
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getUsers();
      setUsers(response.users || []);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Failed to fetch users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const user = getUser();
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    setCurrentUser(user);
    fetchUsers();
    getCompanies()
      .then(response => setCompanies(response.data || []))
      .catch(() => setCompanies([]));
    getAllMachines()
      .then(response => setMachines(response.data || []))
      .catch(() => setMachines([]));
  }, [fetchUsers, navigate]);

  const companyOptions = useMemo(
    () =>
      companies.map(company => ({
        value: company.company_id,
        label: company.name,
      })),
    [companies]
  );

  const machineOptions = useMemo(
    () =>
      machines.map(machine => ({
        value: machine.id,
        label: machine.nickname ? `${machine.nickname} (${machine.serial})` : machine.serial,
      })),
    [machines]
  );

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const closeDrawer = () => {
    if (loading) {
      return;
    }
    setDrawer(null);
    setEditingUser(null);
    setFormError('');
    setCreateForm({ username: '', password: '', role: 'user', company_ids: [], machine_ids: [] });
    setEditForm({
      currentPassword: '',
      newPassword: '',
      newUsername: '',
      role: '',
      company_ids: [],
      machine_ids: [],
    });
  };

  const openUserEditor = user => {
    const initialType = user.username === 'admin' ? 'password' : 'username';
    setEditingUser(user);
    setEditType(initialType);
    setEditForm({
      currentPassword: '',
      newPassword: '',
      newUsername: user.username,
      role: user.role,
      company_ids: user.company_ids || [],
      machine_ids: user.machine_ids || [],
    });
    setFormError('');
    setDrawer('edit');
  };

  const handleCreateUser = async () => {
    const validationError =
      validateUsername(createForm.username.trim()) || validatePassword(createForm.password);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    try {
      setLoading(true);
      clearMessages();
      await createUser({
        username: createForm.username.trim(),
        password: createForm.password,
        role: createForm.role,
        ...(createForm.role === 'partner'
          ? { company_ids: createForm.company_ids, machine_ids: createForm.machine_ids }
          : {}),
      });
      setSuccess(`User ${createForm.username.trim()} created.`);
      setDrawer(null);
      setCreateForm({ username: '', password: '', role: 'user', company_ids: [], machine_ids: [] });
      await fetchUsers();
    } catch (requestError) {
      setFormError(requestError.response?.data?.detail || 'Failed to create user.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    try {
      setLoading(true);
      setFormError('');
      clearMessages();
      if (editType === 'password') {
        const validationError = validatePassword(editForm.newPassword);
        if (validationError) {
          setFormError(validationError);
          return;
        }
        await updateUserPassword(editingUser.username, {
          current_password: editForm.currentPassword,
          new_password: editForm.newPassword,
        });
        setSuccess(`Password updated for ${editingUser.username}.`);
      } else if (editType === 'username') {
        const validationError = validateUsername(editForm.newUsername.trim());
        if (validationError) {
          setFormError(validationError);
          return;
        }
        await updateUsername(editingUser.username, { new_username: editForm.newUsername.trim() });
        if (currentUser?.username === editingUser.username) {
          const updatedUser = { ...currentUser, username: editForm.newUsername.trim() };
          setUser(updatedUser);
          setCurrentUser(updatedUser);
        }
        setSuccess(`Username changed to ${editForm.newUsername.trim()}.`);
      } else {
        await updateUserRole(editingUser.username, {
          role: editForm.role,
          ...(editForm.role === 'partner'
            ? { company_ids: editForm.company_ids, machine_ids: editForm.machine_ids }
            : {}),
        });
        if (currentUser?.username === editingUser.username) {
          const updatedUser = { ...currentUser, role: editForm.role };
          setUser(updatedUser);
          setCurrentUser(updatedUser);
        }
        setSuccess(`Access role updated for ${editingUser.username}.`);
      }
      setDrawer(null);
      setEditingUser(null);
      await fetchUsers();
    } catch (requestError) {
      setFormError(requestError.response?.data?.detail || 'Failed to update user.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async username => {
    try {
      setLoading(true);
      clearMessages();
      await deleteUser(username);
      setSuccess(`User ${username} deleted.`);
      setPendingDelete(null);
      await fetchUsers();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Failed to delete user.');
    } finally {
      setLoading(false);
    }
  };

  const renderScopeEditor = (form, setForm) => (
    <>
      <label>
        <span>Companies in scope</span>
        <Select
          isMulti
          options={companyOptions}
          value={companyOptions.filter(option => form.company_ids.includes(option.value))}
          onChange={selected =>
            setForm(current => ({
              ...current,
              company_ids: (selected || []).map(option => option.value),
            }))
          }
          placeholder="Add companies…"
          classNamePrefix="scope-select"
        />
        <small>Every machine belonging to these companies is included in the scope.</small>
      </label>
      <label>
        <span>Individual machines</span>
        <Select
          isMulti
          options={machineOptions}
          value={machineOptions.filter(option => form.machine_ids.includes(option.value))}
          onChange={selected =>
            setForm(current => ({
              ...current,
              machine_ids: (selected || []).map(option => option.value),
            }))
          }
          placeholder="Add single machines…"
          classNamePrefix="scope-select"
        />
        <small>Machines granted one by one, on top of the company scope.</small>
      </label>
    </>
  );

  const filteredUsers = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return users.filter(
      user =>
        !normalizedQuery ||
        user.username.toLowerCase().includes(normalizedQuery) ||
        user.role.toLowerCase().includes(normalizedQuery)
    );
  }, [query, users]);

  const formatDate = dateString => new Date(dateString).toLocaleString();

  if (!currentUser || currentUser.role !== 'admin') {
    return null;
  }

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Administration</span>
          <h1>Team access</h1>
          <p>Create operator accounts and manage identity, role, and security from one place.</p>
        </div>
        <button type="button" className={styles.primaryButton} onClick={() => setDrawer('create')}>
          <Plus size={16} /> Create user
        </button>
      </header>

      {error && (
        <Toast tone="error" onClose={clearMessages}>
          {error}
        </Toast>
      )}
      {success && (
        <Toast tone="success" onClose={clearMessages}>
          {success}
        </Toast>
      )}

      <section className={styles.summary}>
        <div>
          <Users size={18} />
          <span>
            <strong>{users.length}</strong>
            <small>Total accounts</small>
          </span>
        </div>
        <div>
          <ShieldCheck size={18} />
          <span>
            <strong>{users.filter(user => user.role === 'admin').length}</strong>
            <small>Administrators</small>
          </span>
        </div>
        <div>
          <UserCog size={18} />
          <span>
            <strong>{users.filter(user => user.role === 'user').length}</strong>
            <small>Operators</small>
          </span>
        </div>
        <div>
          <Wrench size={18} />
          <span>
            <strong>{users.filter(user => user.role === 'partner').length}</strong>
            <small>Partners</small>
          </span>
        </div>
      </section>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>User directory</h2>
            <p>The built-in admin account has restricted identity and role controls.</p>
          </div>
          <label className={styles.searchField}>
            <Search size={15} />
            <span className={styles.srOnly}>Search users</span>
            <input
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search username or role"
            />
          </label>
        </div>
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Created</th>
                <th>Last changed</th>
                <th>
                  <span className={styles.srOnly}>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.username}>
                  <td data-label="User">
                    <div className={styles.userIdentity}>
                      <span>{user.username.slice(0, 1).toUpperCase()}</span>
                      <div>
                        <strong>{user.username}</strong>
                        {user.username === currentUser.username && <small>Current account</small>}
                      </div>
                    </div>
                  </td>
                  <td data-label="Role">
                    <span
                      className={`${styles.roleBadge} ${
                        user.role === 'admin'
                          ? styles.adminBadge
                          : user.role === 'partner'
                            ? styles.partnerBadge
                            : ''
                      }`}
                    >
                      {user.role}
                    </span>
                    {user.role === 'partner' && (
                      <small className={styles.companyHint}>
                        {(user.company_ids || []).length} companies ·{' '}
                        {(user.machine_ids || []).length} machines
                      </small>
                    )}
                  </td>
                  <td data-label="Created">{formatDate(user.created_at)}</td>
                  <td data-label="Last changed">{formatDate(user.updated_at)}</td>
                  <td className={styles.actions}>
                    <div className={styles.rowActions}>
                      <button type="button" onClick={() => openUserEditor(user)}>
                        <UserCog size={15} /> Manage
                      </button>
                      {user.username !== 'admin' && user.username !== currentUser.username && (
                        <button
                          type="button"
                          onClick={() => setPendingDelete(user.username)}
                          title="Delete user"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <ConfirmDialog
        open={!!pendingDelete}
        title={`Delete user ${pendingDelete}?`}
        description="The account will lose access immediately. This cannot be undone."
        confirmLabel="Delete user"
        busyLabel="Deleting…"
        busy={loading}
        onConfirm={() => handleDeleteUser(pendingDelete)}
        onCancel={() => setPendingDelete(null)}
      />

      <ActionDrawer
        open={drawer === 'create'}
        eyebrow="Team access"
        title="Create user"
        description="Give a new operator access to Hub Cloud."
        onClose={closeDrawer}
        closeDisabled={loading}
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={closeDrawer}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleCreateUser}
              disabled={loading}
            >
              {loading ? 'Creating…' : 'Create user'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          {formError && <div className={styles.formError}>{formError}</div>}
          <label>
            <span>Username</span>
            <input
              value={createForm.username}
              onChange={event =>
                setCreateForm(current => ({ ...current, username: event.target.value }))
              }
              autoFocus
            />
            <small>3–30 characters; letters, numbers, underscores, and dashes.</small>
          </label>
          <label>
            <span>Initial password</span>
            <input
              type="password"
              value={createForm.password}
              onChange={event =>
                setCreateForm(current => ({ ...current, password: event.target.value }))
              }
            />
            <small>At least 6 characters.</small>
          </label>
          <label>
            <span>Access role</span>
            <select
              value={createForm.role}
              onChange={event =>
                setCreateForm(current => ({ ...current, role: event.target.value }))
              }
            >
              <option value="user">Operator · standard access</option>
              <option value="admin">Administrator · user management access</option>
              <option value="partner">Partner · single-company access</option>
            </select>
          </label>
          {createForm.role === 'partner' && renderScopeEditor(createForm, setCreateForm)}
        </div>
      </ActionDrawer>

      <ActionDrawer
        open={drawer === 'edit'}
        eyebrow="Team access"
        title={`Manage ${editingUser?.username || 'user'}`}
        description="Choose the account property you want to change."
        onClose={closeDrawer}
        closeDisabled={loading}
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={closeDrawer}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleUpdateUser}
              disabled={loading}
            >
              {loading ? 'Saving…' : 'Save change'}
            </button>
          </>
        }
      >
        {editingUser && (
          <div className={styles.drawerForm}>
            <div className={styles.editTabs}>
              {editingUser.username !== 'admin' && (
                <button
                  type="button"
                  className={editType === 'username' ? styles.editTabActive : ''}
                  onClick={() => {
                    setEditType('username');
                    setFormError('');
                  }}
                >
                  <UserCog size={15} /> Identity
                </button>
              )}
              {editingUser.username !== 'admin' && (
                <button
                  type="button"
                  className={editType === 'role' ? styles.editTabActive : ''}
                  onClick={() => {
                    setEditType('role');
                    setFormError('');
                  }}
                >
                  <ShieldCheck size={15} /> Access
                </button>
              )}
              <button
                type="button"
                className={editType === 'password' ? styles.editTabActive : ''}
                onClick={() => {
                  setEditType('password');
                  setFormError('');
                }}
              >
                <KeyRound size={15} /> Security
              </button>
            </div>
            {formError && <div className={styles.formError}>{formError}</div>}
            {editType === 'username' && (
              <label>
                <span>Username</span>
                <input
                  value={editForm.newUsername}
                  onChange={event =>
                    setEditForm(current => ({ ...current, newUsername: event.target.value }))
                  }
                />
              </label>
            )}
            {editType === 'role' && (
              <>
                <label>
                  <span>Access role</span>
                  <select
                    value={editForm.role}
                    onChange={event =>
                      setEditForm(current => ({ ...current, role: event.target.value }))
                    }
                  >
                    <option value="user">Operator · standard access</option>
                    <option value="admin">Administrator · user management access</option>
                    <option value="partner">Partner · single-company access</option>
                  </select>
                </label>
                {editForm.role === 'partner' && renderScopeEditor(editForm, setEditForm)}
              </>
            )}
            {editType === 'password' && (
              <>
                {currentUser.username === editingUser.username && (
                  <label>
                    <span>Current password</span>
                    <input
                      type="password"
                      value={editForm.currentPassword}
                      onChange={event =>
                        setEditForm(current => ({
                          ...current,
                          currentPassword: event.target.value,
                        }))
                      }
                    />
                  </label>
                )}
                <label>
                  <span>New password</span>
                  <input
                    type="password"
                    value={editForm.newPassword}
                    onChange={event =>
                      setEditForm(current => ({ ...current, newPassword: event.target.value }))
                    }
                  />
                  <small>At least 6 characters.</small>
                </label>
              </>
            )}
          </div>
        )}
      </ActionDrawer>
    </div>
  );
}

export default UserManagement;
